// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// https://azure.github.io/azure-sdk/typescript_design.html#ts-config-lib
/// <reference lib="esnext.asynciterable" />

import {
  type AddConfigurationSettingOptions,
  type AddConfigurationSettingParam,
  type AddConfigurationSettingResponse,
  type AppConfigurationClientOptions,
  type CheckConfigurationSettingsOptions,
  type ConfigurationSetting,
  type ConfigurationSettingId,
  type ConfigurationSnapshot,
  type CreateSnapshotOptions,
  type CreateSnapshotResponse,
  type DeleteConfigurationSettingOptions,
  type DeleteConfigurationSettingResponse,
  type GetConfigurationSettingOptions,
  type GetConfigurationSettingResponse,
  type GetSnapshotOptions,
  type GetSnapshotResponse,
  type ListConfigurationSettingPage,
  type ListConfigurationSettingsForSnapshotOptions,
  type ListConfigurationSettingsOptions,
  type ListLabelsOptions,
  type ListLabelsPage,
  type ListRevisionsOptions,
  type ListRevisionsPage,
  type ListSnapshotsOptions,
  type ListSnapshotsPage,
  type PageSettings,
  type SetConfigurationSettingOptions,
  type SetConfigurationSettingParam,
  type SetConfigurationSettingResponse,
  type SetReadOnlyOptions,
  type SetReadOnlyResponse,
  type SettingLabel,
  type SnapshotInfo,
  type UpdateSnapshotOptions,
  type UpdateSnapshotResponse,
} from "./models.js";
import type { PagedAsyncIterableIterator, PagedResult } from "@azure/core-paging";
import { getPagedAsyncIterator } from "@azure/core-paging";
import type { PipelinePolicy, RestError } from "@azure/core-rest-pipeline";
import { bearerTokenAuthenticationPolicyName, createHttpHeaders } from "@azure/core-rest-pipeline";
import { audienceErrorHandlingPolicy } from "./internal/audienceErrorHandlingPolicy.js";
import { SyncTokens, syncTokenPolicy } from "./internal/syncTokenPolicy.js";
import { queryParamPolicy } from "./internal/queryParamPolicy.js";
import { emptyBodyPolicy } from "./internal/emptyBodyPolicy.js";
import type { TokenCredential } from "@azure/core-auth";
import { isTokenCredential } from "@azure/core-auth";
import type {
  SendConfigurationSettingsOptions,
  SendLabelsRequestOptions,
} from "./internal/helpers.js";
import {
  assertResponse,
  checkAndFormatIfAndIfNoneMatch,
  encodePathParameter,
  extractAfterTokenFromLinkHeader,
  extractAfterTokenFromNextLink,
  formatAcceptDateTime,
  formatConfigurationSettingsFiltersAndSelect,
  formatFieldsForSelect,
  formatFiltersAndSelect,
  formatLabelsFiltersAndSelect,
  formatSnapshotFiltersAndSelect,
  getScope,
  makeConfigurationSettingEmpty,
  serializeAsConfigurationSettingParam,
  snapshotInfoToGenerated,
  transformKeyValue,
  transformKeyValueResponse,
  transformKeyValueResponseWithStatusCode,
  transformSnapshotResponse,
} from "./internal/helpers.js";
import {
  AppConfigurationClient as GeneratedAppConfigurationClient,
  type AppConfigurationClientOptionalParams as GeneratedAppConfigurationClientOptionalParams,
} from "./generated/appConfigurationClient.js";
import type {
  _KeyValueListResult,
  _LabelListResult,
  _SnapshotListResult,
} from "./generated/models/models.js";
import {
  _getKeyValuesSend,
  _getKeyValuesDeserialize,
  _checkKeyValuesSend,
  _checkKeyValuesDeserialize,
  _getRevisionsSend,
  _getRevisionsDeserialize,
  _getLabelsSend,
  _getLabelsDeserialize,
  _getSnapshotsSend,
  _getSnapshotsDeserialize,
  _createSnapshotSend,
  _createSnapshotDeserialize,
} from "./generated/api/operations.js";
import { getLongRunningPoller } from "./generated/static-helpers/pollingHelpers.js";
import type { AppConfigurationContext } from "./generated/api/appConfigurationContext.js";
import type { FeatureFlagValue } from "./featureFlag.js";
import type { SecretReferenceValue } from "./secretReference.js";
import type { SnapshotReferenceValue } from "./snapshotReference.js";
import { appConfigKeyCredentialPolicy } from "./appConfigCredential.js";
import { tracingClient } from "./internal/tracing.js";
import { logger } from "./logger.js";
import type { OperationState } from "@azure/core-lro";
import type { SimplePollerLike } from "./internal/lroShim.js";
import { wrapPoller } from "./internal/lroShim.js";
import { appConfigurationApiVersion, packageVersion } from "./internal/constants.js";

const ConnectionStringRegex = /Endpoint=(.*);Id=(.*);Secret=(.*)/;

/**
 * Provides internal configuration options for AppConfigurationClient.
 * @internal
 */
export interface InternalAppConfigurationClientOptions extends AppConfigurationClientOptions {
  /**
   * The sync token cache to use for this client.
   * NOTE: this is an internal option, not for general client usage.
   */
  syncTokens?: SyncTokens;
}

/**
 * Client for the Azure App Configuration service.
 */
export class AppConfigurationClient {
  private client: GeneratedAppConfigurationClient;
  private _syncTokens: SyncTokens;

  /**
   * Initializes a new instance of the AppConfigurationClient class.
   * @param options - Options for the AppConfigurationClient.
   */
  constructor(connectionString: string, options?: AppConfigurationClientOptions);
  /**
   * Initializes a new instance of the AppConfigurationClient class using
   * a TokenCredential.
   * @param endpoint - The endpoint of the App Configuration service (ex: https://sample.azconfig.io).
   * @param tokenCredential - An object that implements the `TokenCredential` interface used to authenticate requests to the service. Use the \@azure/identity package to create a credential that suits your needs.
   * @param options - Options for the AppConfigurationClient.
   */
  constructor(
    endpoint: string,
    tokenCredential: TokenCredential,
    options?: AppConfigurationClientOptions,
  );
  constructor(
    connectionStringOrEndpoint: string,
    tokenCredentialOrOptions?: TokenCredential | AppConfigurationClientOptions,
    options?: AppConfigurationClientOptions,
  ) {
    let appConfigOptions: InternalAppConfigurationClientOptions;
    let appConfigCredential: TokenCredential | undefined = undefined;
    let appConfigEndpoint: string;
    let authPolicy: PipelinePolicy | undefined;
    let authPolicyName: string;
    let scope: [string] | undefined;

    if (isTokenCredential(tokenCredentialOrOptions)) {
      appConfigOptions = (options as InternalAppConfigurationClientOptions) || {};
      appConfigCredential = tokenCredentialOrOptions;
      appConfigEndpoint = connectionStringOrEndpoint.endsWith("/")
        ? connectionStringOrEndpoint.slice(0, -1)
        : connectionStringOrEndpoint;
      scope = [getScope(appConfigEndpoint, appConfigOptions.audience)];
      authPolicyName = bearerTokenAuthenticationPolicyName;
    } else {
      appConfigOptions = (tokenCredentialOrOptions as InternalAppConfigurationClientOptions) || {};
      const regexMatch = connectionStringOrEndpoint?.match(ConnectionStringRegex);
      if (regexMatch) {
        appConfigEndpoint = regexMatch[1];
        authPolicy = appConfigKeyCredentialPolicy(regexMatch[2], regexMatch[3]);
        authPolicyName = authPolicy.name;
      } else {
        throw new Error(
          `Invalid connection string. Valid connection strings should match the regex '${ConnectionStringRegex.source}'.` +
            ` To mitigate the issue, please refer to the troubleshooting guide here at https://aka.ms/azsdk/js/app-configuration/troubleshoot.`,
        );
      }
    }

    const generatedClientOptions: GeneratedAppConfigurationClientOptionalParams = {
      ...appConfigOptions,
      userAgentOptions: {
        ...appConfigOptions.userAgentOptions,
        userAgentPrefix: appConfigOptions.userAgentOptions?.userAgentPrefix
          ? `${appConfigOptions.userAgentOptions.userAgentPrefix} azsdk-js-app-configuration/${packageVersion}`
          : `azsdk-js-app-configuration/${packageVersion}`,
      },
      loggingOptions: {
        logger: logger.info,
      },
      apiVersion: options?.apiVersion ?? appConfigurationApiVersion,
      credentials: {},
    };

    generatedClientOptions.credentials = {
      ...generatedClientOptions.credentials,
      scopes: scope,
    };

    this._syncTokens = appConfigOptions.syncTokens || new SyncTokens();
    this.client = new GeneratedAppConfigurationClient(
      appConfigEndpoint,
      // When a connection string is used, appConfigCredential is undefined here. We pass undefined to avoid @azure-rest/core-client's keyCredentialAuthenticationPolicy setting the apiKeyHeader on the request.
      // Connection strings are authenticated by HMAC (appConfigKeyCredentialPolicy) and the secret should never leave the client.
      // The `as TokenCredential` cast bridges a gap between the generated client and the core SDK: the generated constructor types `credential` as required (KeyCredential | TokenCredential),
      // but @azure-rest/core-client's addCredentialPipelinePolicy no-ops when no credential is passed, so handing it undefined is safe at runtime.
      appConfigCredential as TokenCredential,
      generatedClientOptions,
    );
    this.client.pipeline.addPolicy(
      audienceErrorHandlingPolicy(appConfigOptions?.audience !== undefined),
      {
        phase: "Sign",
        beforePolicies: [authPolicyName],
      },
    );
    if (authPolicy) {
      this.client.pipeline.addPolicy(authPolicy, { phase: "Sign" });
    }

    this.client.pipeline.addPolicy(queryParamPolicy());
    this.client.pipeline.addPolicy(emptyBodyPolicy());
    this.client.pipeline.addPolicy(syncTokenPolicy(this._syncTokens), { afterPhase: "Retry" });
  }

  /**
   * Add a setting into the Azure App Configuration service, failing if it
   * already exists.
   *
   * Example usage:
   * ```ts snippet:AddConfigurationSetting
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const result = await client.addConfigurationSetting({
   *   key: "MyKey",
   *   label: "MyLabel",
   *   value: "MyValue",
   * });
   * ```
   * @param configurationSetting - A configuration setting.
   * @param options - Optional parameters for the request.
   */
  addConfigurationSetting(
    configurationSetting:
      | AddConfigurationSettingParam
      | AddConfigurationSettingParam<FeatureFlagValue>
      | AddConfigurationSettingParam<SecretReferenceValue>
      | AddConfigurationSettingParam<SnapshotReferenceValue>,
    options: AddConfigurationSettingOptions = {},
  ): Promise<AddConfigurationSettingResponse> {
    return tracingClient.withSpan(
      "AppConfigurationClient.addConfigurationSetting",
      options,
      async (updatedOptions) => {
        const keyValue = serializeAsConfigurationSettingParam(configurationSetting);
        logger.info("[addConfigurationSetting] Creating a key value pair");
        try {
          const originalResponse = await this.client.putKeyValue(
            "application/json",
            encodePathParameter(configurationSetting.key),
            {
              ifNoneMatch: "*",
              label: configurationSetting.label,
              entity: keyValue,
              ...updatedOptions,
              requestOptions: {
                ...updatedOptions.requestOptions,
                skipUrlEncoding: true,
              },
            },
          );
          const response = transformKeyValueResponse(originalResponse);
          assertResponse(response);
          return response;
        } catch (error) {
          const err = error as RestError;
          // Service does not return an error message. Raise a 412 error similar to .NET
          if (err.statusCode === 412) {
            err.message = `Status 412: Setting was already present`;
          }
          throw err;
        }
        throw new Error("Unreachable code");
      },
    );
  }

  /**
   * Delete a setting from the Azure App Configuration service
   *
   * Example usage:
   * ```ts snippet:DeleteConfigurationSetting
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const deletedSetting = await client.deleteConfigurationSetting({
   *   key: "MyKey",
   *   label: "MyLabel",
   * });
   * ```
   * @param id - The id of the configuration setting to delete.
   * @param options - Optional parameters for the request (ex: etag, label)
   */
  deleteConfigurationSetting(
    id: ConfigurationSettingId,
    options: DeleteConfigurationSettingOptions = {},
  ): Promise<DeleteConfigurationSettingResponse> {
    return tracingClient.withSpan(
      "AppConfigurationClient.deleteConfigurationSetting",
      options,
      async (updatedOptions) => {
        let status;
        logger.info("[deleteConfigurationSetting] Deleting key value pair");
        const originalResponse = await this.client.deleteKeyValue(encodePathParameter(id.key), {
          label: id.label,
          ...updatedOptions,
          ...checkAndFormatIfAndIfNoneMatch(id, options),
          onResponse: (response) => {
            status = response.status;
          },
          requestOptions: {
            ...updatedOptions.requestOptions,
            skipUrlEncoding: true,
          },
        });

        const response = transformKeyValueResponseWithStatusCode(originalResponse, status);
        assertResponse(response);
        return response;
      },
    );
  }

  /**
   * Gets a setting from the Azure App Configuration service.
   *
   * Example code:
   * ```ts snippet:GetConfigurationSetting
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const setting = await client.getConfigurationSetting({ key: "MyKey", label: "MyLabel" });
   * ```
   * @param id - The id of the configuration setting to get.
   * @param options - Optional parameters for the request.
   */
  async getConfigurationSetting(
    id: ConfigurationSettingId,
    options: GetConfigurationSettingOptions = {},
  ): Promise<GetConfigurationSettingResponse> {
    return tracingClient.withSpan(
      "AppConfigurationClient.getConfigurationSetting",
      options,
      async (updatedOptions) => {
        let status;
        let rawResponse: any;
        logger.info("[getConfigurationSetting] Getting key value pair");
        try {
          const originalResponse = await this.client.getKeyValue(encodePathParameter(id.key), {
            ...updatedOptions,
            label: id.label,
            select: formatFieldsForSelect(options.fields),
            ...formatAcceptDateTime(options),
            ...checkAndFormatIfAndIfNoneMatch(id, options),
            onResponse: (response) => {
              status = response.status;
              rawResponse = response;
            },
            requestOptions: {
              ...updatedOptions.requestOptions,
              skipUrlEncoding: true,
            },
          });

          const response = transformKeyValueResponseWithStatusCode(originalResponse, status);
          assertResponse(response);
          return response;
        } catch (error) {
          const err = error as RestError;
          // 304 only comes back if the user has passed a conditional option in their
          // request _and_ the remote object has the same etag as what the user passed.
          if (err.statusCode === 304) {
            const response = transformKeyValueResponseWithStatusCode(
              { _response: rawResponse } as any,
              304,
            );
            // this is one of our few 'required' fields so we'll make sure it does get initialized
            // with a value
            response.key = id.key;
            // and now we'll undefine all the other properties that are not HTTP related
            makeConfigurationSettingEmpty(response);
            assertResponse(response);
            return response;
          }
          throw err;
        }
      },
    );
  }

  /**
   * Lists settings from the Azure App Configuration service, optionally
   * filtered by key names, labels and accept datetime.
   *
   * Example code:
   * ```ts snippet:ListConfigurationSettings
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const allSettingsWithLabel = client.listConfigurationSettings({ labelFilter: "MyLabel" });
   * ```
   * @param options - Optional parameters for the request.
   */
  listConfigurationSettings(
    options: ListConfigurationSettingsOptions = {},
  ): PagedAsyncIterableIterator<ConfigurationSetting, ListConfigurationSettingPage, PageSettings> {
    const pageEtags = options.pageEtags ? [...options.pageEtags] : undefined;
    delete options.pageEtags;
    const pagedResult: PagedResult<ListConfigurationSettingPage, PageSettings, string | undefined> =
      {
        firstPageLink: undefined,
        getPage: async (pageLink: string | undefined) => {
          const etag = pageEtags?.shift();
          try {
            const response = await this.sendConfigurationSettingsRequest(
              { ...options, etag },
              pageLink,
            );
            const currentResponse: ListConfigurationSettingPage = {
              ...response,
              items: response.items != null ? response.items?.map(transformKeyValue) : [],
              continuationToken: response.nextLink
                ? extractAfterTokenFromNextLink(response.nextLink)
                : undefined,
              _response: response._response,
            };
            return {
              page: currentResponse,
              nextPageLink: currentResponse.continuationToken,
            };
          } catch (error) {
            const err = error as RestError;

            const link = err.response?.headers?.get("link");
            const continuationToken = link ? extractAfterTokenFromLinkHeader(link) : undefined;

            if (err.statusCode === 304) {
              err.message = `Status 304: No updates for this page`;
              logger.info(
                `[listConfigurationSettings] No updates for this page. The current etag for the page is ${etag}`,
              );
              return {
                page: {
                  items: [],
                  etag,
                  _response: { ...err.response, status: 304 },
                } as unknown as ListConfigurationSettingPage,
                nextPageLink: continuationToken,
              };
            }

            throw err;
          }
        },
        toElements: (page) => page.items,
      };
    return getPagedAsyncIterator(pagedResult);
  }

  /**
   * Checks settings from the Azure App Configuration service using a HEAD request, returning only headers without the response body.
   * This is useful for efficiently checking if settings have changed by comparing ETags.
   *
   * Example code:
   * ```ts snippet:CheckConfigurationSettings
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const pageIterator = client.checkConfigurationSettings({ keyFilter: "MyKey" }).byPage();
   * ```
   * @param options - Optional parameters for the request.
   */
  checkConfigurationSettings(
    options: CheckConfigurationSettingsOptions = {},
  ): PagedAsyncIterableIterator<ConfigurationSetting, ListConfigurationSettingPage, PageSettings> {
    const pageEtags = options.pageEtags ? [...options.pageEtags] : undefined;
    delete options.pageEtags;
    const pagedResult: PagedResult<ListConfigurationSettingPage, PageSettings, string | undefined> =
      {
        firstPageLink: undefined,
        getPage: async (pageLink: string | undefined) => {
          const etag = pageEtags?.shift();
          try {
            const response = await this.checkConfigurationSettingsRequest(
              { ...options, etag },
              pageLink,
            );
            const link = response._response?.headers?.get("link") as string | undefined;
            const continuationToken = link ? extractAfterTokenFromLinkHeader(link) : undefined;
            const currentResponse: ListConfigurationSettingPage = {
              ...response,
              etag: response._response?.headers?.get("etag") as string | undefined,
              items: [],
              continuationToken: continuationToken,
              _response: response._response,
            };
            return {
              page: currentResponse,
              nextPageLink: currentResponse.continuationToken,
            };
          } catch (error) {
            const err = error as RestError;

            const link = err.response?.headers?.get("link");
            const continuationToken = link ? extractAfterTokenFromLinkHeader(link) : undefined;

            if (err.statusCode === 304) {
              err.message = `Status 304: No updates for this page`;
              logger.info(
                `[checkConfigurationSettings] No updates for this page. The current etag for the page is ${etag}`,
              );
              return {
                page: {
                  items: [],
                  etag,
                  _response: { ...err.response, status: 304 },
                } as unknown as ListConfigurationSettingPage,
                nextPageLink: continuationToken,
              };
            }

            throw err;
          }
        },
        toElements: (page) => page.items,
      };
    return getPagedAsyncIterator(pagedResult);
  }

  /**
   * Lists settings from the Azure App Configuration service for snapshots based on name, optionally
   * filtered by key names, labels and accept datetime.
   *
   * Example code:
   * ```ts snippet:ListConfigurationSettingsForSnashots
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const allSettingsWithLabel = client.listConfigurationSettingsForSnashots({
   *   snapshotName: "MySnapshot",
   * });
   * ```
   * @param options - Optional parameters for the request.
   */
  listConfigurationSettingsForSnapshot(
    snapshotName: string,
    options: ListConfigurationSettingsForSnapshotOptions = {},
  ): PagedAsyncIterableIterator<ConfigurationSetting, ListConfigurationSettingPage, PageSettings> {
    const pagedResult: PagedResult<ListConfigurationSettingPage, PageSettings, string | undefined> =
      {
        firstPageLink: undefined,
        getPage: async (pageLink: string | undefined) => {
          const response = await this.sendConfigurationSettingsRequest(
            { snapshotName, ...options },
            pageLink,
          );
          const currentResponse = {
            ...response,
            items: response.items != null ? response.items?.map(transformKeyValue) : [],
            continuationToken: response.nextLink
              ? extractAfterTokenFromNextLink(response.nextLink)
              : undefined,
          };
          return {
            page: currentResponse,
            nextPageLink: currentResponse.continuationToken,
          };
        },
        toElements: (page) => page.items,
      };
    return getPagedAsyncIterator(pagedResult);
  }

  /**
   * Get a list of labels from the Azure App Configuration service
   *
   * Example code:
   * ```ts snippet:ListLabels
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const allSettingsWithLabel = client.listLabels({ nameFilter: "prod*" });
   * ```
   * @param options - Optional parameters for the request.
   */
  listLabels(
    options: ListLabelsOptions = {},
  ): PagedAsyncIterableIterator<SettingLabel, ListLabelsPage, PageSettings> {
    const pagedResult: PagedResult<ListLabelsPage, PageSettings, string | undefined> = {
      firstPageLink: undefined,
      getPage: async (pageLink: string | undefined) => {
        const response = await this.sendLabelsRequest(options, pageLink);
        const currentResponse: ListLabelsPage = {
          ...response,
          items: response.items ?? [],
          continuationToken: response.nextLink
            ? extractAfterTokenFromNextLink(response.nextLink)
            : undefined,
          _response: response._response,
        };
        return {
          page: currentResponse,
          nextPageLink: currentResponse.continuationToken,
        };
      },
      toElements: (page) => page.items,
    };
    return getPagedAsyncIterator(pagedResult);
  }

  private get _context(): AppConfigurationContext {
    return (this.client as any)._client as AppConfigurationContext;
  }

  private async sendLabelsRequest(
    options: SendLabelsRequestOptions & PageSettings = {},
    pageLink: string | undefined,
  ): Promise<_LabelListResult & { _response: any }> {
    return tracingClient.withSpan(
      "AppConfigurationClient.listConfigurationSettings",
      options,
      async (updatedOptions) => {
        const rawResponse = await _getLabelsSend(this._context, {
          ...updatedOptions,
          ...formatAcceptDateTime(options),
          ...formatLabelsFiltersAndSelect(options),
          after: pageLink,
          requestOptions: {
            ...updatedOptions.requestOptions,
            skipUrlEncoding: true,
          },
        });
        const parsed = await _getLabelsDeserialize(rawResponse);
        return Object.assign(parsed, { _response: rawResponse });
      },
    );
  }

  private async sendConfigurationSettingsRequest(
    options: SendConfigurationSettingsOptions & PageSettings = {},
    pageLink: string | undefined,
  ): Promise<_KeyValueListResult & { _response: any }> {
    return tracingClient.withSpan(
      "AppConfigurationClient.listConfigurationSettings",
      options,
      async (updatedOptions) => {
        const rawResponse = await _getKeyValuesSend(this._context, {
          ...updatedOptions,
          ...formatAcceptDateTime(options),
          ...formatConfigurationSettingsFiltersAndSelect(options),
          ...checkAndFormatIfAndIfNoneMatch({ etag: options.etag }, { onlyIfChanged: true }),
          after: pageLink,
          requestOptions: {
            ...updatedOptions.requestOptions,
            skipUrlEncoding: true,
          },
        });
        const parsed = await _getKeyValuesDeserialize(rawResponse);
        return Object.assign(parsed, { _response: rawResponse });
      },
    );
  }

  private async checkConfigurationSettingsRequest(
    options: SendConfigurationSettingsOptions & PageSettings = {},
    pageLink: string | undefined,
  ): Promise<{ _response: any }> {
    return tracingClient.withSpan(
      "AppConfigurationClient.checkConfigurationSettings",
      options,
      async (updatedOptions) => {
        const rawResponse = await _checkKeyValuesSend(this._context, {
          ...updatedOptions,
          ...formatAcceptDateTime(options),
          ...formatConfigurationSettingsFiltersAndSelect(options),
          ...checkAndFormatIfAndIfNoneMatch({ etag: options.etag }, { onlyIfChanged: true }),
          after: pageLink,
          requestOptions: {
            ...updatedOptions.requestOptions,
            skipUrlEncoding: true,
          },
        });
        await _checkKeyValuesDeserialize(rawResponse);
        return {
          _response: {
            ...rawResponse,
            headers: createHttpHeaders(rawResponse.headers),
          },
        };
      },
    );
  }

  /**
   * Lists revisions of a set of keys, optionally filtered by key names,
   * labels and accept datetime.
   *
   * Example code:
   * ```ts snippet:ListRevisions
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const revisionsIterator = client.listRevisions({ keys: ["MyKey"] });
   * ```
   * @param options - Optional parameters for the request.
   */
  listRevisions(
    options?: ListRevisionsOptions,
  ): PagedAsyncIterableIterator<ConfigurationSetting, ListRevisionsPage, PageSettings> {
    const pagedResult: PagedResult<ListRevisionsPage, PageSettings, string | undefined> = {
      firstPageLink: undefined,
      getPage: async (pageLink: string | undefined) => {
        const response = await this.sendRevisionsRequest(options, pageLink);
        const currentResponse = {
          ...response,
          items: response.items != null ? response.items.map(transformKeyValue) : [],
          continuationToken: response.nextLink
            ? extractAfterTokenFromNextLink(response.nextLink)
            : undefined,
        };
        // let itemList = currentResponse.items;
        return {
          page: currentResponse,
          nextPageLink: currentResponse.continuationToken,
        };
      },
      toElements: (page) => page.items,
    };
    return getPagedAsyncIterator(pagedResult);
  }

  private async sendRevisionsRequest(
    options: ListConfigurationSettingsOptions & PageSettings = {},
    pageLink: string | undefined,
  ): Promise<_KeyValueListResult & { _response: any }> {
    return tracingClient.withSpan(
      "AppConfigurationClient.listRevisions",
      options,
      async (updatedOptions) => {
        const rawResponse = await _getRevisionsSend(this._context, {
          ...updatedOptions,
          ...formatAcceptDateTime(options),
          ...formatFiltersAndSelect(updatedOptions),
          after: pageLink,
          requestOptions: {
            ...updatedOptions.requestOptions,
            skipUrlEncoding: true,
          },
        });
        const parsed = await _getRevisionsDeserialize(rawResponse);
        return Object.assign(parsed, { _response: rawResponse });
      },
    );
  }

  /**
   * Sets the value of a key in the Azure App Configuration service, allowing for an optional etag.
   * @param key - The name of the key.
   * @param configurationSetting - A configuration value.
   * @param options - Optional parameters for the request.
   *
   * Example code:
   * ```ts snippet:SetConfigurationSetting
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * await client.setConfigurationSetting({ key: "MyKey", value: "MyValue" });
   * ```
   */
  async setConfigurationSetting(
    configurationSetting:
      | SetConfigurationSettingParam
      | SetConfigurationSettingParam<FeatureFlagValue>
      | SetConfigurationSettingParam<SecretReferenceValue>
      | SetConfigurationSettingParam<SnapshotReferenceValue>,
    options: SetConfigurationSettingOptions = {},
  ): Promise<SetConfigurationSettingResponse> {
    return tracingClient.withSpan(
      "AppConfigurationClient.setConfigurationSetting",
      options,
      async (updatedOptions) => {
        const keyValue = serializeAsConfigurationSettingParam(configurationSetting);
        logger.info("[setConfigurationSetting] Setting new key value");
        const response = transformKeyValueResponse(
          await this.client.putKeyValue(
            "application/json",
            encodePathParameter(configurationSetting.key),
            {
              ...updatedOptions,
              label: configurationSetting.label,
              entity: keyValue,
              ...checkAndFormatIfAndIfNoneMatch(configurationSetting, options),

              requestOptions: {
                ...updatedOptions.requestOptions,
                skipUrlEncoding: true,
              },
            },
          ),
        );
        assertResponse(response);
        return response;
      },
    );
  }

  /**
   * Sets or clears a key's read-only status.
   * @param id - The id of the configuration setting to modify.
   */
  async setReadOnly(
    id: ConfigurationSettingId,
    readOnly: boolean,
    options: SetReadOnlyOptions = {},
  ): Promise<SetReadOnlyResponse> {
    return tracingClient.withSpan(
      "AppConfigurationClient.setReadOnly",
      options,
      async (newOptions) => {
        let response;
        if (readOnly) {
          logger.info("[setReadOnly] Setting read-only status to ${readOnly}");
          response = await this.client.putLock(encodePathParameter(id.key), {
            ...newOptions,
            label: id.label,
            ...checkAndFormatIfAndIfNoneMatch(id, options),

            requestOptions: {
              ...newOptions.requestOptions,
              skipUrlEncoding: true,
            },
          });
        } else {
          logger.info("[setReadOnly] Deleting read-only lock");
          response = await this.client.deleteLock(encodePathParameter(id.key), {
            ...newOptions,
            label: id.label,
            ...checkAndFormatIfAndIfNoneMatch(id, options),

            requestOptions: {
              ...newOptions.requestOptions,
              skipUrlEncoding: true,
            },
          });
        }
        response = transformKeyValueResponse(response);
        assertResponse(response);
        return response;
      },
    );
  }

  /**
   * Adds an external synchronization token to ensure service requests receive up-to-date values.
   *
   * @param syncToken - The synchronization token value.
   */
  updateSyncToken(syncToken: string): void {
    this._syncTokens.addSyncTokenFromHeaderValue(syncToken);
  }

  /**
   * Begins creating a snapshot for Azure App Configuration service, fails if it
   * already exists.
   */
  beginCreateSnapshot(
    snapshot: SnapshotInfo,
    // eslint-disable-next-line @azure/azure-sdk/ts-naming-options
    options: CreateSnapshotOptions = {},
  ): Promise<SimplePollerLike<OperationState<CreateSnapshotResponse>, CreateSnapshotResponse>> {
    return tracingClient.withSpan(
      `${AppConfigurationClient.name}.beginCreateSnapshot`,
      options,
      async (updatedOptions) => {
        const generatedSnapshot = snapshotInfoToGenerated(snapshot);
        const poller = getLongRunningPoller(
          this._context,
          async (result) =>
            transformSnapshotResponse(
              await _createSnapshotDeserialize(result),
            ) as CreateSnapshotResponse,
          ["201", "200", "202"],
          {
            updateIntervalInMs: updatedOptions?.updateIntervalInMs,
            abortSignal: updatedOptions?.abortSignal,
            getInitialResponse: () =>
              _createSnapshotSend(
                this._context,
                "application/vnd.microsoft.appconfig.snapshot+json",
                snapshot.name,
                generatedSnapshot,
                {
                  ...updatedOptions,
                  requestOptions: {
                    ...updatedOptions.requestOptions,
                    skipUrlEncoding: true,
                  },
                },
              ),
            resourceLocationConfig: "original-uri",
          },
        );
        return wrapPoller(poller);
      },
    );
  }

  /**
   * Begins creating a snapshot for Azure App Configuration service, waits until it is done,
   * fails if it already exists.
   */
  beginCreateSnapshotAndWait(
    snapshot: SnapshotInfo,
    // eslint-disable-next-line @azure/azure-sdk/ts-naming-options
    options: CreateSnapshotOptions = {},
  ): Promise<CreateSnapshotResponse> {
    return tracingClient.withSpan(
      `${AppConfigurationClient.name}.beginCreateSnapshotAndWait`,
      options,
      async (updatedOptions) => {
        const generatedSnapshot = snapshotInfoToGenerated(snapshot);
        const poller = getLongRunningPoller(
          this._context,
          async (result) =>
            transformSnapshotResponse(
              await _createSnapshotDeserialize(result),
            ) as CreateSnapshotResponse,
          ["201", "200", "202"],
          {
            updateIntervalInMs: updatedOptions?.updateIntervalInMs,
            abortSignal: updatedOptions?.abortSignal,
            getInitialResponse: () =>
              _createSnapshotSend(
                this._context,
                "application/vnd.microsoft.appconfig.snapshot+json",
                snapshot.name,
                generatedSnapshot,
                {
                  ...updatedOptions,
                  requestOptions: {
                    ...updatedOptions.requestOptions,
                    skipUrlEncoding: true,
                  },
                },
              ),
            resourceLocationConfig: "original-uri",
          },
        );
        return poller.pollUntilDone();
      },
    );
  }

  /**
   * Get a snapshot from Azure App Configuration service
   *
   * Example usage:
   * ```ts snippet:GetSnapshot
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const retrievedSnapshot = await client.getSnapshot("testsnapshot");
   * console.log("Retrieved snapshot:", retrievedSnapshot);
   * ```
   * @param name - The name of the snapshot.
   * @param options - Optional parameters for the request.
   */
  getSnapshot(name: string, options: GetSnapshotOptions = {}): Promise<GetSnapshotResponse> {
    return tracingClient.withSpan(
      "AppConfigurationClient.getSnapshot",
      options,
      async (updatedOptions) => {
        logger.info("[getSnapshot] Get a snapshot");
        const originalResponse = await this.client.getSnapshot(name, {
          ...updatedOptions,
          requestOptions: {
            ...updatedOptions.requestOptions,
            skipUrlEncoding: true,
          },
        });
        const response = transformSnapshotResponse(originalResponse);
        assertResponse(response);
        return response;
      },
    );
  }

  /**
   * Recover an archived snapshot back to ready status
   *
   * Example usage:
   * ```ts snippet:RecoverSnapshot
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const result = await client.recoverSnapshot("MySnapshot");
   * ```
   * @param name - The name of the snapshot.
   * @param options - Optional parameters for the request.
   */
  recoverSnapshot(
    name: string,
    // eslint-disable-next-line @azure/azure-sdk/ts-naming-options
    options: UpdateSnapshotOptions = {},
  ): Promise<UpdateSnapshotResponse> {
    return tracingClient.withSpan(
      "AppConfigurationClient.recoverSnapshot",
      options,
      async (updatedOptions) => {
        logger.info("[recoverSnapshot] Recover a snapshot");
        const originalResponse = await this.client.updateSnapshot(
          "application/merge-patch+json",
          name,
          { status: "ready" },
          {
            ...updatedOptions,
            ...checkAndFormatIfAndIfNoneMatch(
              { etag: options.etag },
              { onlyIfUnchanged: true, ...options },
            ),
            requestOptions: {
              ...updatedOptions.requestOptions,
              skipUrlEncoding: true,
            },
          },
        );
        const response = transformSnapshotResponse(originalResponse);
        assertResponse(response);
        return response;
      },
    );
  }
  /**
   * Archive a ready snapshot
   *
   * Example usage:
   * ```ts snippet:ArchiveSnapshot
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const result = await client.archiveSnapshot({ name: "MySnapshot" });
   * ```
   * @param name - The name of the snapshot.
   * @param options - Optional parameters for the request.
   */
  archiveSnapshot(
    name: string,
    // eslint-disable-next-line @azure/azure-sdk/ts-naming-options
    options: UpdateSnapshotOptions = {},
  ): Promise<UpdateSnapshotResponse> {
    return tracingClient.withSpan(
      "AppConfigurationClient.archiveSnapshot",
      options,
      async (updatedOptions) => {
        logger.info("[archiveSnapshot] Archive a snapshot");
        const originalResponse = await this.client.updateSnapshot(
          "application/merge-patch+json",
          name,
          { status: "archived" },
          {
            ...updatedOptions,
            ...checkAndFormatIfAndIfNoneMatch(
              { etag: options.etag },
              { onlyIfUnchanged: true, ...options },
            ),
            requestOptions: {
              ...updatedOptions.requestOptions,
              skipUrlEncoding: true,
            },
          },
        );
        const response = transformSnapshotResponse(originalResponse);
        assertResponse(response);
        return response;
      },
    );
  }

  /**
   * List all snapshots from Azure App Configuration service
   *
   * Example usage:
   * ```ts snippet:ListSnapshots
   * import { DefaultAzureCredential } from "@azure/identity";
   * import { AppConfigurationClient } from "@azure/app-configuration";
   *
   * // The endpoint for your App Configuration resource
   * const endpoint = "https://example.azconfig.io";
   * const credential = new DefaultAzureCredential();
   * const client = new AppConfigurationClient(endpoint, credential);
   *
   * const snapshots = await client.listSnapshots();
   *
   * for await (const snapshot of snapshots) {
   *   console.log(`Found snapshot: ${snapshot.name}`);
   * }
   * ```
   * @param options - Optional parameters for the request.
   */
  listSnapshots(
    options: ListSnapshotsOptions = {},
  ): PagedAsyncIterableIterator<ConfigurationSnapshot, ListSnapshotsPage, PageSettings> {
    const pagedResult: PagedResult<ListSnapshotsPage, PageSettings, string | undefined> = {
      firstPageLink: undefined,
      getPage: async (pageLink: string | undefined) => {
        const response = await this.sendSnapShotsRequest(options, pageLink);
        const currentResponse = {
          ...response,
          items:
            response.items != null ? response.items.map((s) => transformSnapshotResponse(s)) : [],
          continuationToken: response.nextLink
            ? extractAfterTokenFromNextLink(response.nextLink)
            : undefined,
        };
        return {
          page: currentResponse,
          nextPageLink: currentResponse.continuationToken,
        };
      },
      toElements: (page) => page.items,
    };
    return getPagedAsyncIterator(pagedResult);
  }

  private async sendSnapShotsRequest(
    options: ListSnapshotsOptions & PageSettings = {},
    pageLink: string | undefined,
  ): Promise<_SnapshotListResult & { _response: any }> {
    return tracingClient.withSpan(
      "AppConfigurationClient.listSnapshots",
      options,
      async (updatedOptions) => {
        const rawResponse = await _getSnapshotsSend(this._context, {
          ...updatedOptions,
          ...formatSnapshotFiltersAndSelect(options),
          after: pageLink,
          requestOptions: {
            ...updatedOptions.requestOptions,
            skipUrlEncoding: true,
          },
        });
        const parsed = await _getSnapshotsDeserialize(rawResponse);
        return Object.assign(parsed, { _response: rawResponse });
      },
    );
  }
}
